<template>
  <div class="chart-container">
    <figure role="group" :aria-labelledby="'chart-title-' + _uid">
      <figcaption :id="'chart-title-' + _uid" class="sr-only">
        {{ $t('monitoring.chart_status_distribution_title') }}
      </figcaption>
      
      <div :aria-hidden="true">
        <pie-chart
            ref="pieChartAll"
            :chart-data="pieChartDataAll"
            :options="chartOptionsAll">
        </pie-chart>
      </div>
      
      <table class="sr-only" :aria-label="$t('monitoring.chart_data_table_label')">
        <caption>{{ $t('monitoring.chart_status_distribution_detail') }}</caption>
        <thead>
          <tr>
            <th scope="col">{{ $t('monitoring.status_label') }}</th>
            <th scope="col">{{ $t('monitoring.count_label') }}</th>
            <th scope="col">{{ $t('monitoring.percentage_label') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="pieChartDataAll">
            <th scope="row">{{ $t('monitoring.status_all_test_ok') }}</th>
            <td>{{ pieChartDataAll.datasets[0].data[0] }}</td>
            <td>{{ getPercentage(pieChartDataAll.datasets[0].data[0]) }}%</td>
          </tr>
          <tr v-if="pieChartDataAll">
            <th scope="row">{{ $t('monitoring.status_no_enrollment') }}</th>
            <td>{{ pieChartDataAll.datasets[0].data[1] }}</td>
            <td>{{ getPercentage(pieChartDataAll.datasets[0].data[1]) }}%</td>
          </tr>
          <tr v-if="pieChartDataAll">
            <th scope="row">{{ $t('monitoring.status_planned') }}</th>
            <td>{{ pieChartDataAll.datasets[0].data[2] }}</td>
            <td>{{ getPercentage(pieChartDataAll.datasets[0].data[2]) }}%</td>
          </tr>
          <tr v-if="pieChartDataAll">
            <th scope="row">{{ $t('monitoring.status_in_testing') }}</th>
            <td>{{ pieChartDataAll.datasets[0].data[3] }}</td>
            <td>{{ getPercentage(pieChartDataAll.datasets[0].data[3]) }}%</td>
          </tr>
        </tbody>
      </table>
    </figure>
  </div>
</template>

<script>
import {Pie} from "vue-chartjs";
import ChartDataLabels from "chartjs-plugin-datalabels";


export default {
  name: 'ChartPieStat',
  components: {
    "pie-chart": {
      extends: Pie,
      props: ["chartData", "options"],
      mounted() {
        this.addPlugin(ChartDataLabels)
        this.renderChart(this.chartData, this.options)
      }
    }
  },
  props: {
    stat: {
    },
  },
  computed: {
  },
  data () {
    return {
      chartOptionsAll: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          datalabels: {
            formatter: (value, ctx) => {
              let total = ctx.chart.data.datasets[0].data.reduce(
                  (a, b) => a + b,
                  0
              )
              let percentage = ''
              if (total !== 0 && value !== 0) {
                percentage = ((value / total) * 100).toFixed(2) + "%"
              }
              return percentage
            },
            color: "#000",
            font: {
              weight: "bold",
            },
          }
        }
      },
      pieChartDataAll: null,

    }
  },
  created () {
  },
  mounted () {
    this.generatePieChartDataStatus(this.stat)
  },
  beforeDestroy () {
  },
  methods: {
    generatePieChartDataStatus(data) {
      let statusCounts = {
        AllTestOk: 0,
        NoEnrollment: 0,
        Planned: 0,
        InTesting: 0,
      };

      data.forEach((item) => {
        if (parseInt(item.TotalTest) === parseInt(item.TotalOk)) {
          statusCounts.AllTestOk++
        } else if (!item.StartDate && !item.EndDate) {
          statusCounts.NoEnrollment++
        } else if (item.StartDate && !item.EndDate) {
          statusCounts.Planned++
        } else if (item.StartDate && item.EndDate) {
          statusCounts.InTesting++
        }
      });

      this.pieChartDataAll = {
        labels: ["All Test Ok", "No Enrollment", "Planned", "In Testing"],
        datasets: [
          {
            data: [
              statusCounts.AllTestOk,
              statusCounts.NoEnrollment,
              statusCounts.Planned,
              statusCounts.InTesting,
            ],
            backgroundColor: [
              this.getCustomColor("AllTestOk", 0.8),
              this.getCustomColor("NoEnrollment", 0.8),
              this.getCustomColor("Planned", 0.8),
              this.getCustomColor("InTesting", 0.8),
            ],
          },
        ],
      }

      this.$nextTick(() => {
        if (this.$refs.pieChartAll) {
          this.$refs.pieChartAll.renderChart(
              this.pieChartDataAll,
              this.chartOptionsAll
          )
        }
      })
    },
    getCustomColor(key, alpha = 1) {
      let r, g, b
      // Suppression des couleurs aléatoires pour assurer des contrastes constants
      switch (key) {
        case 'InTesting': // Orange WCAG AA compatible
          r = 182;
          g = 112;
          b = 0;
          break;
        case 'RUN': // Violet WCAG AA compatible  
          r = 95;
          g = 39;
          b = 205;
          break;
        case 'OK': // Bleu WCAG AA compatible
          r = 0;
          g = 97;
          b = 161;
          break;
        case 'connector 3': // Gris WCAG AA compatible
        case 'NoEnrollment': // Gris WCAG AA compatible
          r = 108;
          g = 108;
          b = 108;
          break;
        case 'CONF_NOT_FOUND': // Vert WCAG AA compatible
          r = 0;
          g = 121;
          b = 89;
          break;
        case 'Planned': // Bleu profond WCAG AA compatible
          r = 0;
          g = 80;
          b = 132;
          break;
        case 'KO': // Brun WCAG AA compatible
          r = 166;
          g = 68;
          b = 0;
          break;
        case 'connector 7': // Cyan WCAG AA compatible
          r = 0;
          g = 130;
          b = 130;
          break;
        case 'AllTestOk': // Vert foncé WCAG AA compatible
          r = 0;
          g = 102;
          b = 41;
          break;
        case 'NULL': // Rouge WCAG AA compatible
          r = 153;
          g = 0;
          b = 0;
          break;
        default:
          // Couleur de fallback WCAG AA compatible
          r = 85;
          g = 85;
          b = 85;
          break;
      }
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    },
    
    // Nouvelle méthode pour calculer les pourcentages
    getPercentage(value) {
      if (!this.pieChartDataAll || !this.pieChartDataAll.datasets[0]) return 0
      const total = this.pieChartDataAll.datasets[0].data.reduce((a, b) => a + b, 0)
      if (total === 0 || value === 0) return 0
      return ((value / total) * 100).toFixed(1)
    }
  }
}
</script>

<style scoped>
/* Classe pour masquer visuellement mais garder accessible aux lecteurs d'écran */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Conteneur du graphique avec rôle d'image pour l'accessibilité */
.chart-container {
  position: relative;
}

/* Tableau de données accessible */
.chart-data-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
}

.chart-data-table th,
.chart-data-table td {
  border: 1px solid #ccc;
  padding: 8px;
  text-align: left;
}

.chart-data-table th {
  background-color: #f5f5f5;
  font-weight: bold;
}

.chart-data-table caption {
  font-weight: bold;
  margin-bottom: 0.5rem;
  text-align: left;
}

/* Indicateur visuel de couleur dans le tableau */
.status-indicator {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
  border: 1px solid #666;
}

/* Classes existantes mises à jour avec des couleurs WCAG AA */
.result-vignette {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
  border: 1px solid #666;
}

.result-mtch {
  background-color: rgba(0, 102, 41, 0.8);
}

.result-nmtc {
  background-color: rgba(153, 0, 0, 0.8);
}

.result-cmtc {
  background-color: rgba(182, 112, 0, 0.8);
}

.result-noap {
  background-color: rgba(108, 108, 108, 0.8);
}
</style>
